import { Stack } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import RequestServiceIcon from '@mui/icons-material/Add';
import { SortingDirection } from 'common/types/SortingOptions';

import { CustomerJob } from 'domain/entities/CustomerJob';
import { Entity } from 'domain/types/Entity';
import { useCaseSearchCustomerJobs } from 'application/jobs/useCases/useCaseSearchCustomerJobs';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { PageTitle } from 'targets/web/modules/dashboard/components';
import { Button, Loader, VirtualizedGrid } from 'targets/web/components';
import Alert from 'targets/web/components/Alert';
import CustomerPortalBanner from 'targets/customer-portal/modules/portal/components/CustomerPortalBanner';
import JobFilters from 'targets/customer-portal/modules/jobs/components/JobFilters';
import { CustomerJobLogCard } from 'targets/customer-portal/modules/jobs/components/CustomerJobLogCard';
import RequestServiceModal from 'targets/customer-portal/modules/jobs/components/RequestServiceModal';
import {
  PageType,
  useCustomerJobViewFilters,
} from 'targets/customer-portal/modules/jobs/hooks/useCustomerJobViewFilters';
import RequestedServiceDetailsModal from 'targets/customer-portal/modules/jobs/components/RequestedServiceDetailsModal';
import { CancelJobRequestConfirmation } from 'targets/customer-portal/modules/jobs/components/CancelJobRequestConfirmation';

const CustomerJobsPage: FC<{ type: PageType }> = ({ type }) => {
  const t = useTranslationPrefix('customer_portal.jobs_page');
  const { filters, effectiveFilters, setFilters } = useCustomerJobViewFilters(type);
  const {
    allPages: jobs,
    fetchNextPage: fetchNextJobsPage,
    hasNextPage: hasNextJobsPage,
    isFetching: isFetchingJobs,
  } = useCaseSearchCustomerJobs({
    stationIds: effectiveFilters.stationIds,
    customerId: effectiveFilters.customerId,
    status: effectiveFilters.status,
    phrase: effectiveFilters.phrase,
    sortBy: 'status',
    direction: SortingDirection.Ascending,
  });

  const loadMoreJobs = useCallback(() => {
    if (hasNextJobsPage && !isFetchingJobs) {
      fetchNextJobsPage();
    }
  }, [fetchNextJobsPage, hasNextJobsPage, isFetchingJobs]);

  const virtualizedGridProps = useMemo(
    () => ({
      useWindowScroll: true,
      spacing: 4,
      minColumnWidth: 280,
      endReached: loadMoreJobs,
      overscan: 500,
    }),
    [loadMoreJobs],
  );

  const [requestServiceModalOpen, setRequestServiceModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Entity<CustomerJob>>();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const showSpinner = isFetchingJobs && !jobs.length;

  return (
    <Stack direction="column" spacing={4} width="100%">
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={4}
        justifyContent="space-between"
        width="100%"
      >
        <PageTitle
          title={type === 'active-jobs' ? t('active_jobs_title') : t('completed_jobs_title')}
        />
        <Button
          onClick={() => setRequestServiceModalOpen(true)}
          size="large"
          color="primary"
          startIcon={<RequestServiceIcon />}
        >
          {t('request_service_btn')}
        </Button>
        <RequestServiceModal
          onClose={() => setRequestServiceModalOpen(false)}
          open={requestServiceModalOpen}
        />
        <RequestedServiceDetailsModal
          onClose={() => setDetailsModalOpen(false)}
          open={detailsModalOpen}
          job={selectedJob}
        />
        <CancelJobRequestConfirmation
          open={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          jobRequestId={selectedJob?.id}
          onSuccess={() => {
            setCancelModalOpen(false);
          }}
        />
      </Stack>
      <CustomerPortalBanner />
      <JobFilters
        initialValues={filters}
        pageType={type}
        onFiltersChange={(newFilters) => {
          setFilters((filters) => ({
            ...filters,
            ...newFilters,
          }));
        }}
      />
      <Alert
        hideCloseButton
        severity="info"
        message={t('changes_require_cancelling_message')}
      ></Alert>

      <Stack
        gap={4}
        sx={{
          position: 'relative',
          width: 1,
          height: 1,
        }}
      >
        {showSpinner ? (
          <Stack height={1} width={1} alignItems="center" justifyContent="center">
            <Loader size={48} />
          </Stack>
        ) : (
          <VirtualizedGrid
            {...virtualizedGridProps}
            data={jobs}
            computeItemKey={(_index, element) => element.id}
            itemContent={(_index, element) => (
              <CustomerJobLogCard
                job={element}
                key={element.id}
                onViewDetailsClick={() => {
                  setSelectedJob(element);
                  setDetailsModalOpen(true);
                }}
                onCancelClick={() => {
                  setSelectedJob(element);
                  setCancelModalOpen(true);
                }}
              />
            )}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default CustomerJobsPage;
